import { DashboardEvent, DashboardEventKind, kindOf } from '../types/events.js';
import { BUCKET_COUNT } from '../types/constants.js';
import { VolumeChart } from './chart.js';
import { BILLING_TYPES, CostByBillingType, CostLedger, formatUnitCost } from './costs.js';
import { collectDom } from './dom.js';
import { clearFeed, pushFeed } from './feed.js';
import { PlaybackEngine } from './engine.js';
import { ParticleField } from './particles.js';
import { Roster, RosterSortMode } from './roster.js';
import { ensureAuthenticated } from './auth.js';

// 認証 (本番ビルドでのみ動作。dev ローカルは AUTH_ENABLED=false で素通り)
ensureAuthenticated();
import { installPlaybackUi } from './playback-ui.js';
import { SoundController, pickMilestoneSound } from './sounds.js';
import {
  globalCounts,
  noteIncomingEvent,
  resetGlobalCounts,
  resetTimeState,
  virtualNowMs,
} from './state.js';
import { fmtHms } from './utils.js';

const dom = collectDom();

const roster = new Roster(dom, {
  bucketCount: BUCKET_COUNT,
  onNewBadgeExpire: () => {
    /* noop: badge は roster 内で消される */
  },
});

const chart = new VolumeChart(dom, {
  onRoll: (direction) => {
    roster.rollAllSparks(direction);
  },
});

const particles = new ParticleField(dom.bg);

const sounds = new SoundController();
function refreshVolumeProgress(): void {
  const pct = Math.round(sounds.getVolume() * 100);
  dom.soundVolumeProgress.style.width = `${pct}%`;
}
function refreshSoundToggle(): void {
  const muted = sounds.isMuted();
  dom.soundToggle.classList.toggle('is-muted', muted);
  dom.soundToggle.setAttribute('aria-pressed', muted ? 'false' : 'true');
  dom.soundVolume.disabled = muted;
  dom.soundVolumeWrap.classList.toggle('is-disabled', muted);
}
dom.soundVolume.value = String(Math.round(sounds.getVolume() * 100));
refreshVolumeProgress();
refreshSoundToggle();
dom.soundToggle.addEventListener('click', () => {
  sounds.toggle();
  refreshSoundToggle();
});

dom.soundVolume.addEventListener('input', () => {
  const n = Number(dom.soundVolume.value);
  if (!Number.isFinite(n)) return;
  sounds.setVolume(n / 100);
  refreshVolumeProgress();
});

const FLASH_COLORS: Readonly<Record<'received' | 'processed' | 'failed', string>> = {
  received: 'rgba(255, 168, 74, 0.9)',
  processed: 'rgba(62, 232, 198, 0.9)',
  failed: 'rgba(255, 106, 106, 0.9)',
};

function triggerFlash(kind: 'received' | 'processed' | 'failed'): void {
  dom.flash.style.setProperty('--flash-color', FLASH_COLORS[kind]);
  dom.flash.classList.remove('is-firing');
  void dom.flash.offsetWidth;
  dom.flash.classList.add('is-firing');
  window.setTimeout(() => dom.flash.classList.remove('is-firing'), 80);
}

function bumpCard(kind: 'received' | 'failed'): void {
  const card = kind === 'received' ? dom.cards.success : dom.cards.failure;
  card.card.classList.remove('is-firing');
  void card.card.offsetWidth;
  card.card.classList.add('is-firing');
  window.setTimeout(() => card.card.classList.remove('is-firing'), 1100);
  card.value.classList.remove('is-bumping');
  void card.value.offsetWidth;
  card.value.classList.add('is-bumping');
  window.setTimeout(() => card.value.classList.remove('is-bumping'), 360);
}

function incrementGlobal(kind: 'received' | 'failed'): void {
  if (kind === 'received') {
    globalCounts.success += 1;
    dom.countSuccess.textContent = String(globalCounts.success);
  } else {
    globalCounts.failure += 1;
    dom.countFailure.textContent = String(globalCounts.failure);
  }
  dom.totalCount.textContent = String(globalCounts.success + globalCounts.failure);
}

// rawDataId ごとの「最後に見た確定状態」。
// raw_data.updated_at は status 以外の更新 (ページ回転など) でも動くため、同じ確定イベントが
// 後続のポーリング範囲で再送されることがある。同じ rawDataId + 同じ状態の再送は、FAILURES 加算や
// feed / flash などの副作用ごと無視して冪等にする。状態が変わった (FAILED → 再実行で PROCESSED 等)
// 場合は新しい遷移として扱う。seek / 日付変更で globalCounts と一緒にリセットする。
const settledKinds = new Map<string, DashboardEventKind>();

/** 初めて見る確定 (rawDataId, kind) なら記録して true。既に同じ確定を見ていれば false。 */
function markSettled(event: DashboardEvent): boolean {
  const kind = kindOf(event);
  const id = event.metadata.rawDataId;
  if (settledKinds.get(id) === kind) return false;
  settledKinds.set(id, kind);
  return true;
}

// コストの台帳。確定イベントが流れるたびに rawDataId を渡し、/costs/range の結果と突き合わせる。
const costs = new CostLedger();

function costLines(tallies: CostByBillingType | null): string[] {
  if (tallies === null) return [];
  const lines: string[] = [];
  for (const billingType of BILLING_TYPES) {
    const text = formatUnitCost(billingType, tallies[billingType]);
    if (text !== null) lines.push(text);
  }
  return lines;
}

/** 1 行 = 1 span で書く。中身が前回と同じなら DOM に触らない。 */
function setLines(el: HTMLElement, lines: ReadonlyArray<string>): void {
  const key = lines.join('\n');
  if (el.dataset['lines'] === key) return;
  el.dataset['lines'] = key;
  el.replaceChildren(
    ...lines.map((text) => {
      const span = document.createElement('span');
      span.textContent = text;
      return span;
    }),
  );
}

// 行は並べ替えで作り直されることがあるので、変化の有無によらず定期的に書き直す (差分が無ければ何もしない)。
function renderCosts(): void {
  // ヘッダは全社ぶんを 1 行で: "$0.069/枚 · $0.028/件"。まだ 1 件も積めていなければ "—"。
  const total = costs.getTotal();
  const totalLines = costLines(total);
  dom.costTotal.textContent = totalLines.length > 0 ? totalLines.join(' · ') : '—';
  for (const c of roster.getCustomers().values()) {
    setLines(c.costEl, costLines(costs.getCompany(c.name)));
  }
}
window.setInterval(renderCosts, 500);

// route-D 社内 (metadata.isInternal) の読み込みを含めるか。既定は「省く」(顧客の動きを見る画面のため)。
// ブラウザごとに localStorage で覚える。
const INCLUDE_INTERNAL_KEY = 'fsg.includeInternal';
let includeInternal = window.localStorage.getItem(INCLUDE_INTERNAL_KEY) === '1';

function refreshInternalToggle(): void {
  dom.internalToggle.classList.toggle('is-including', includeInternal);
  dom.internalToggle.setAttribute('aria-pressed', includeInternal ? 'true' : 'false');
  dom.internalToggle.textContent = includeInternal ? 'route-D 含む' : 'route-D 省く';
}
refreshInternalToggle();

const timestamps: number[] = [];
function recordForRate(): void {
  timestamps.push(Date.now());
}
function updateRate(): void {
  const wallNow = Date.now();
  // 受信レート (eps) は wall 時間ベース (実時間で何件来てるか)
  while (timestamps.length > 0) {
    const head = timestamps[0];
    if (head === undefined || head >= wallNow - 10_000) break;
    timestamps.shift();
  }
  dom.rateLabel.textContent = (timestamps.length / 10).toFixed(1);

  // "just now" / "Ns ago" は virtual 時刻ベース (再生速度に追従)
  const vNow = virtualNowMs();
  if (vNow === null) return;
  for (const c of roster.getCustomers().values()) {
    const diff = Math.max(0, Math.floor((vNow - c.lastAt) / 1000));
    c.timeEl.textContent =
      diff < 5
        ? 'just now'
        : diff < 60
          ? `${diff}s ago`
          : diff < 3600
            ? `${Math.floor(diff / 60)}m ago`
            : `${Math.floor(diff / 3600)}h ago`;
  }
}
window.setInterval(updateRate, 500);

function updateChartNow(): void {
  const v = virtualNowMs();
  dom.chartNow.textContent = v === null ? '--:--:--' : fmtHms(v);
  // engine の cursor (virtual time) に合わせて chart の last bucket を進める。
  // イベントが来ない時間帯でも 10 分おきにバーが流れる挙動を保証する。
  const cur = engine.getCursorMs();
  if (cur !== null) chart.advanceTo(cur);
  // ACTIVE CUSTOMERS の社数は roster の MAX_ROSTER で trim される前の真の窓内ユニーク数を出す
  dom.uniqueCount.textContent = String(chart.getActiveCompanyCount());
}
window.setInterval(updateChartNow, 250);

function dispatch(event: DashboardEvent, silent: boolean): void {
  if (!silent) noteIncomingEvent(event.timestamp);

  switch (event.eventName) {
    case 'RawDataUpload': {
      const prevSuccess = globalCounts.success;
      incrementGlobal('received');
      chart.addEvent(event);
      roster.touchCustomer(event, chart.bucketCount);
      roster.addItemDot(event);
      if (!silent) {
        triggerFlash('received');
        bumpCard('received');
        pushFeed(dom, {
          kind: 'received',
          at: event.timestamp,
          customer: event.metadata.companyName,
          body: event.metadata.documentName,
        });
        particles.spawn('received', 1);
        // 100 件跨ぎは levelup で塗り替える。それ以外は通常チャイム。
        const stepSound = pickMilestoneSound(prevSuccess, globalCounts.success);
        sounds.play(stepSound ?? 'event');
      }
      return;
    }

    case 'RawDataConversionSucceeded': {
      if (!markSettled(event)) return;
      costs.noteSettled(event.metadata.rawDataId, event.metadata.companyName);
      roster.updateItemState(event.metadata.rawDataId, 'processed');
      return;
    }

    case 'RawData.JobFailed': {
      if (!markSettled(event)) return;
      costs.noteSettled(event.metadata.rawDataId, event.metadata.companyName);
      incrementGlobal('failed');
      const customer = roster.updateItemState(event.metadata.rawDataId, 'failed');
      if (!silent) {
        triggerFlash('failed');
        bumpCard('failed');
        pushFeed(dom, {
          kind: 'failed',
          at: event.timestamp,
          customer: customer ? customer.name : event.metadata.companyName,
          body: event.metadata.failedReason ?? 'failed',
        });
        particles.spawn('failed', 0.6);
      }
      return;
    }

    case 'RawData.JobCancelled': {
      if (!markSettled(event)) return;
      costs.noteSettled(event.metadata.rawDataId, event.metadata.companyName);
      const customer = roster.updateItemState(event.metadata.rawDataId, 'cancelled');
      if (!silent) {
        pushFeed(dom, {
          kind: 'cancelled',
          at: event.timestamp,
          customer: customer ? customer.name : event.metadata.companyName,
          body: event.metadata.cancelReason ?? 'cancelled',
        });
      }
      return;
    }

    case 'RawData.NoTarget': {
      // 読取り対象が 1 件も無かった (BE の NO_TARGET)。失敗ではないので flash / 失敗カウントはしない。
      // Upload が窓外 / 未ロードでも feed には 1 回だけ出す (Cancelled / Failed と同じ扱い)。
      if (!markSettled(event)) return;
      costs.noteSettled(event.metadata.rawDataId, event.metadata.companyName);
      const customer = roster.updateItemState(event.metadata.rawDataId, 'noTarget');
      if (!silent) {
        pushFeed(dom, {
          kind: 'noTarget',
          at: event.timestamp,
          customer: customer ? customer.name : event.metadata.companyName,
          body: 'no target',
        });
      }
      return;
    }
  }
}

function countedDispatch(event: DashboardEvent, silent: boolean): void {
  // 省くモードでは社内イベントを全ビュー (chart / roster / feed / カウント / 音) から落とす。
  if (!includeInternal && event.metadata.isInternal === true) return;
  if (!silent) recordForRate();
  dispatch(event, silent);
}

const engine = new PlaybackEngine({
  bucketVirtualMs: chart.bucketVirtualMs,
  dispatch: countedDispatch,
  onSeek: () => {
    resetGlobalCounts();
    settledKinds.clear();
    costs.reset();
    resetTimeState();
    dom.countSuccess.textContent = '0';
    dom.countFailure.textContent = '0';
    dom.totalCount.textContent = '0';
    roster.clear();
    clearFeed(dom);
    chart.reset();
    updateChartNow();
  },
  onSilentBegin: () => {
    chart.beginBatch();
    roster.beginBatch();
  },
  onSilentEnd: () => {
    chart.endBatch();
    roster.endBatch();
  },
  onRangeFetched: (fromMs, toMs) => costs.requestRange(fromMs, toMs),
});

installPlaybackUi(engine);

// トグル時は現在位置まで再生し直して画面を作り直す (イベントは再取得するが、モードの切替だけで
// 過去の集計も入れ替わる)。LIVE 中は LIVE のまま、再生中は同じ位置・同じ速度で続ける。
dom.internalToggle.addEventListener('click', () => {
  includeInternal = !includeInternal;
  window.localStorage.setItem(INCLUDE_INTERNAL_KEY, includeInternal ? '1' : '0');
  refreshInternalToggle();
  const cursor = engine.getCursorMs();
  if (cursor === null) return;
  dom.internalToggle.disabled = true;
  void (async () => {
    try {
      if (engine.isLive()) {
        await engine.enterLive();
      } else {
        await engine.seek(cursor);
      }
    } finally {
      dom.internalToggle.disabled = false;
    }
  })();
});

function parseSortMode(v: string): RosterSortMode | null {
  if (v === 'recency' || v === 'count') return v;
  return null;
}
dom.rosterSort.addEventListener('change', () => {
  const m = parseSortMode(dom.rosterSort.value);
  if (m) roster.setSortMode(m);
});
